
import { supabase } from "@/integrations/supabase/client";
import { getActiveUniversity } from "@/utils/prize/universityUtils";

interface SubmitTipParams {
  title: string;
  description: string;
  emoji: string;
  addedBy: string;
  status: string;
}

export const tipsApi = {
  fetchApprovedTips: async () => {
    console.log('Fetching approved tips from database');
    try {
      // First get the active university
      const activeUniversity = await getActiveUniversity();
      console.log('Active university:', activeUniversity);

      // Determine the ordering column based on active university
      const universityName = activeUniversity.name.toUpperCase();
      let orderColumn = 'unsw_votes'; // Default to UNSW

      // Set ordering column based on active university
      switch (universityName) {
        case 'UNSW':
          orderColumn = 'unsw_votes';
          break;
        case 'USYD':
          orderColumn = 'usyd_votes';
          break;
        case 'UOA':
          orderColumn = 'uoa_votes';
          break;
        default:
          console.warn('Unknown university name:', universityName);
          orderColumn = 'unsw_votes'; // Default fallback
      }
      
      console.log(`Ordering tips by ${orderColumn} for ${universityName}`);

      const { data, error } = await supabase
        .from('tips')
        .select('*')
        .eq('status', 'approved')
        .eq('archived', false)
        .or(`scope.eq.all_universities,and(scope.eq.university_specific,university_id.eq.${activeUniversity.id})`)
        .order(orderColumn, { ascending: false });

      if (error) {
        console.error('Error fetching tips:', error);
        throw error;
      }

      console.log('Successfully fetched tips:', data);
      return data || [];
    } catch (error) {
      console.error('Error in fetchApprovedTips:', error);
      throw error;
    }
  },

  fetchAllTips: async () => {
    console.log('Fetching all tips');
    try {
      const { data, error } = await supabase
        .from('tips')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching all tips:', error);
        throw error;
      }

      console.log('Successfully fetched all tips:', data);
      return data || [];
    } catch (error) {
      console.error('Error in fetchAllTips:', error);
      throw error;
    }
  },

  submitTip: async ({ title, description, emoji, addedBy, status }: SubmitTipParams) => {
    console.log('Submitting new tip:', { title, description, emoji, addedBy });
    try {
      // First get the active university
      const activeUniversity = await getActiveUniversity();
      
      const { data, error } = await supabase
        .from('tips')
        .insert([{
          title,
          content: description,
          emoji,
          status,
          name: addedBy,
          unsw_votes: 0,
          usyd_votes: 0,
          uoa_votes: 0,
          scope: activeUniversity.id ? 'university_specific' : 'all_universities',
          university_id: activeUniversity.id || null
        }])
        .select()
        .single();

      if (error) {
        console.error('Error submitting tip:', error);
        throw error;
      }

      console.log('Successfully submitted tip:', data);
      return data;
    } catch (error) {
      console.error('Error in submitTip:', error);
      throw error;
    }
  },

  updateTipVotes: async (tipId: number) => {
    console.log('Incrementing votes for tip:', tipId);
    try {
      // Get active university
      const activeUniversity = await getActiveUniversity();
      const universityName = activeUniversity.name.toUpperCase();
      
      console.log('Incrementing votes for university:', universityName);
      
      const { data, error } = await supabase
        .rpc('increment_tip_votes', {
          tip_id_param: tipId,
          university_name: universityName
        });

      if (error) {
        console.error('Error incrementing tip votes:', error);
        throw error;
      }

      if (!data || !Array.isArray(data) || data.length === 0) {
        console.error('Invalid response from increment_tip_votes');
        throw new Error('Invalid response from vote increment');
      }

      const updatedTip = data[0];
      console.log('Successfully incremented tip votes. Updated tip:', updatedTip);
      return updatedTip;
    } catch (error) {
      console.error('Error in updateTipVotes:', error);
      throw error;
    }
  }
};
